import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { usePlatformAPI } from "../PlatformProvider";
import { createClientApi } from "./clients";
import type { InfoConfig, UpdateInfoConfigBody } from "./departments";
import type { Result } from "../PlatformClient";
import type { APIError } from "../APIError";

function unwrap<T>(result: Result<T>): T {
    const [data, err] = result;
    if (err) throw err;
    return data as T;
}

export function useClientApi() {
    const client = usePlatformAPI();
    return useMemo(() => createClientApi(client), [client]);
}

export const clientKeys = {
    all: ["clients"] as const,
    infoConfig: (id: string) => [...clientKeys.all, id, "info-config"] as const,
};

export function useClientInfoConfig(id: string) {
    const api = useClientApi();
    return useQuery(
        clientKeys.infoConfig(id),
        () => api.getInfoConfig(id).then(unwrap),
        { enabled: !!id },
    );
}

export function useUpdateClientInfoConfig(clientID: string) {
    const api = useClientApi();
    const qc = useQueryClient();
    return useMutation<InfoConfig, APIError, UpdateInfoConfigBody>(
        (body) => api.updateInfoConfig(clientID, body).then(unwrap),
        { onSuccess: () => qc.invalidateQueries(clientKeys.infoConfig(clientID)) },
    );
}

export function useUploadClientInfoConfigFile(clientID: string) {
    const api = useClientApi();
    const qc = useQueryClient();
    return useMutation<InfoConfig, APIError, { file: File; label?: string }>(
        ({ file, label }) => api.uploadInfoConfigFile(clientID, file, label).then(unwrap),
        { onSuccess: () => qc.invalidateQueries(clientKeys.infoConfig(clientID)) },
    );
}

export function useDeleteClientInfoConfigFile(clientID: string) {
    const api = useClientApi();
    const qc = useQueryClient();
    return useMutation<void, APIError, string>(
        (fileID) => api.deleteInfoConfigFile(clientID, fileID).then(unwrap),
        { onSuccess: () => qc.invalidateQueries(clientKeys.infoConfig(clientID)) },
    );
}
